import { Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DetailInput, SelectCharacter } from '~/components/CreateNewCharacter';
import CustomizeAvaturn from '~/components/CreateNewCharacter/CustomizeAvaturn/CustomizeAvaturn';
import CustomizeUpload from '~/components/CreateNewCharacter/CustomizeUpload/CustomizeUpload';
import FilterAvaturn from '~/components/CreateNewCharacter/FilterAvaturn/FilterAvaturn';
import StepComplete from '~/components/CreateNewCharacter/SelectCharacter/StepComplete/StepComplete';
import { FullScreenLoading, HelmetHead } from '~/components/common';
import { useGetListForCreationQuery } from '~/features/character';
import { PAGE_ROUTE } from '~/constants';
import useCreateNewCharacterStore, {
  CreateNewCharacterStates,
} from '~/store/useCreateNewCharacterStore';

type Step =
  | 'SelectCharacter'
  | 'CustomizeUpload'
  | 'CustomizeAvaturn'
  | 'FilterAvaturn'
  | 'SetProfile'
  | 'Complete';

export type Gender = 'male' | 'female';

/**
 * step - 기본 SelectCharacter
 * 1. SelectCharacter Preset -> SetProfile -> Complete
 * 2. SelectCharacter Upload -> CustomizeUpload -> SetProfile -> Complete
 * 3. SelectCharacter Avaturn -> CustomizeAvaturn -> FilterAvaturn -> SetProfile -> Complete
 */

const CreateNewCharacterContent = () => {
  const [step, setStep] = useState<Step>('SelectCharacter');
  const [gender, setGender] = useState<Gender>('male');

  const setGenerateType = useCreateNewCharacterStore(
    (state) => state.setGenerateType,
  );

  const navigate = useNavigate();

  const { data: characterListData, isPending } = useGetListForCreationQuery();

  const uploadType = useCreateNewCharacterStore((state) => state.generateType);
  const resetAll = useCreateNewCharacterStore((state) => state.resetAll);

  const handleSelectNextStep = (
    type: CreateNewCharacterStates['generateType'],
  ) => {
    if (type === 'preset') {
      setGenerateType('preset');
      setStep('SetProfile');
    } else if (type === 'user') {
      setGenerateType('user');
      setStep('CustomizeUpload');
    } else if (type === 'avaturn') {
      // Generate Avatar
      setGenerateType('avaturn');
      setStep('CustomizeAvaturn');
    }
  };

  const handleDetailInputPrevStep = () => {
    if (uploadType === 'preset') {
      setStep('SelectCharacter');
    } else if (uploadType === 'avaturn') {
      setStep('FilterAvaturn');
    } else if (uploadType === 'user') {
      setStep('CustomizeUpload');
    } else {
      setStep('SelectCharacter');
    }
  };

  const renderPageByStep = (step: Step) => {
    switch (step) {
      case 'SelectCharacter':
        return (
          <SelectCharacter
            onNextStep={handleSelectNextStep}
            characterList={characterListData}
          />
        );
      case 'CustomizeUpload':
        return (
          <CustomizeUpload
            onPrevStep={() => setStep('SelectCharacter')}
            onNextStep={() => setStep('SetProfile')}
          />
        );
      case 'CustomizeAvaturn':
        return (
          <Suspense fallback={<FullScreenLoading />}>
            <CustomizeAvaturn
              gender={gender}
              setGender={setGender}
              onPrevStep={() =>
                characterListData.length === 0
                  ? navigate(PAGE_ROUTE.MAIN)
                  : setStep('SelectCharacter')
              }
              onNextStep={() => setStep('FilterAvaturn')}
            />
          </Suspense>
        );
      case 'FilterAvaturn':
        return (
          <Suspense fallback={<FullScreenLoading />}>
            <FilterAvaturn
              onPrevStep={() => setStep('CustomizeAvaturn')}
              onNextStep={() => setStep('SetProfile')}
            />
          </Suspense>
        );

      case 'SetProfile':
        return (
          <DetailInput
            gender={gender}
            onPrevStep={handleDetailInputPrevStep}
            onNextStep={() => setStep('Complete')}
          />
        );
      case 'Complete':
        return <StepComplete />;
      default:
        return <></>;
    }
  };
  useEffect(() => {
    return () => {
      resetAll();
    };
  }, []);

  useEffect(() => {
    if (!isPending && characterListData.length === 0) {
      // 생성한 아바타가 없는 경우 => 바로 아바타 생성 페이지로 이동
      setGenerateType('avaturn');
      setStep('CustomizeAvaturn');
    }
  }, [isPending, characterListData]);

  return <>{renderPageByStep(step)}</>;
};

export default function CreateNewCharacter() {
  return (
    <>
      <HelmetHead title='New Character' />
      <Suspense fallback={<FullScreenLoading />}>
        <CreateNewCharacterContent />
      </Suspense>
    </>
  );
}
